class _UserAPI {
  constructor() {
    this.includes = [
      'address',
      'equipment',
      'equipmentCustoms',
      'organizations.addresses',
      'organizations.billingInfo',
      'organizations.billingInfo.address',
      'organizations.identifications',
      'organizations.paymentInfo',
      'productSubtypes',
      'profile',
      'profile.links',
      'profilePicture',
      'roles.permissions',
      'spokenLanguages',
    ];
    this.authIncludes = [
      'organizations.addresses',
      'productSubtypes',
      'profile_picture',
    ];
    this.versionPrefix = '/v2';
    this.url = `${this.versionPrefix}/members/me`;
  }

  /**
   * Initialize.
   * TEMPORARY:
   *    The whole axios / auth should be replaced by proper install & usage.
   * @param {object} $axios - @nuxtjs/axios instance
   */
  init($axios) {
    this.API = $axios;
  }

  async fetchMembersMe() {
    try {
      const response = await this.API.get(this.url, {
        params: {
          include: this.includes.join(','),
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async fetchAuthUser() {
    try {
      const response = await this.API.get(this.url, {
        params: {
          include: this.authIncludes.join(','),
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async fetchUserOrganization() {
    try {
      const response = await this.API.get(this.url, {
        params: {
          include: ['organizations', 'organizations.billingInfo'].join(','),
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async fetchMemberStats() {
    try {
      const response = await this.API.get(`${this.versionPrefix}/image-creators/stats`);
      return Promise.resolve(response?.data?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateMemberWelcomed(id) {
    try {
      const response = await this.API.patch(`${this.versionPrefix}/members/${id}`, {
        data: {
          id,
          type: 'member',
          attributes: {
            welcomed: true,
          },
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * export function create({ attributes, recaptcha }, { config = {} } = {}) {
   *   return $create('members', {
   *     type: 'member',
   *     attributes,
   *     meta: {
   *       recaptcha,
   *     },
   *   }, config);
   * }
   */

  /**
   * Create an IC member
   * @param attributes
   * @param meta
   * @returns {Promise<unknown>}
   */
  async createMember(attributes, meta) {
    try {
      const response = await this.API.post(`${this.versionPrefix}/members`, {
        data: {
          type: 'member',
          attributes,
          meta,
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Accept Terms of Services
   * @returns {Promise<unknown>}
   */
  async acceptTos() {
    try {
      const response = await this.API.post(`${this.versionPrefix}/members/tos-validate`);
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Verify an email
   * @param token
   * @returns {Promise<unknown>}
   */
  async verifyEmail(token) {
    try {
      const response = await this.API.post(`${this.versionPrefix}/members/verify-email`, {
        data: {
          type: 'member',
          attributes: {
            token,
          },
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Send a message to the user with a link to reset the password
   * @param email
   * @returns {Promise<unknown>}
   */
  async forgotPassword(email) {
    try {
      const response = await this.API.post(`${this.versionPrefix}/members/forgot-password`, {
        data: {
          type: 'member',
          attributes: {
            email,
          },
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * attributes: {
   *               token: this.token,
   *               email: data.email,
   *               password: data.password,
   *             }
   * @param payload
   * @returns {Promise<unknown>}
   */
  async resetPassword(payload) {
    try {
      const response = await this.API.post(`${this.versionPrefix}/members/reset-password`, {
        data: {
          type: 'member',
          attributes: payload,
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async uploadMemberAvatar(picture) {
    try {
      const formData = new FormData();
      formData.append('data[type]', 'user_profile_picture');
      formData.append('data[attributes][picture]', picture);
      const response = await this.API.post(`${this.versionPrefix}/image-creators/profile/picture`, formData);
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async removeMemberAvatar() {
    try {
      const response = await this.API.delete(`${this.versionPrefix}/image-creators/profile/picture`, null);
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateMember(id, attributes) {
    try {
      const response = await this.API.patch(`${this.versionPrefix}/members/${id}`, {
        data: {
          id,
          type: 'member',
          attributes,
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateMemberProfile(id, attributes, relationships) {
    try {
      const response = await this.API.patch(`${this.versionPrefix}/members/${id}/profile`, {
        data: {
          type: 'profile',
          attributes,
          relationships,
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateSpokenLanguages(id, languages) {
    try {
      const response = await this.API.put(`${this.versionPrefix}/members/${id}/relationships/spoken-languages`, {
        data: languages,
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   *
   * @param userId - UserId
   * @param equipment - Array of equipment uuids
   * @returns {Promise<unknown>}
   */
  async updateUserEquipment(userId, equipment) {
    try {
      const response = await this.API.put(`${this.versionPrefix}/members/${userId}/relationships/equipment`, {
        data: equipment.map((id) => ({
          type: 'equipment',
          id,
        })),
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async fetchMemberPayoneerServiceUrl(payoneerUrl) {
    try {
      const response = await this.API.get(payoneerUrl);
      return Promise.resolve(response?.data?.data?.attributes?.url);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async memberRegisterPayoneer(id) {
    try {
      const response = await this.API.patch(`${this.versionPrefix}/members/${id}`, {
        data: {
          id,
          type: 'member',
          attributes: {
            payoneer_status: 'PENDING_REGISTRATION',
          },
        },
      });
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getMemberInvitation(id) {
    try {
      const response = await this.API.get(`${this.versionPrefix}/invitations/${id}`);
      return Promise.resolve(response?.data?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async resendVerificationEmail() {
    try {
      const response = await this.API.post(`${this.versionPrefix}/members/resend-verification-email`);
      return Promise.resolve(response?.data?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

const UserAPI = new _UserAPI();

export default UserAPI;
